import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaUser, FaSave, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';
import { customerService } from '../../services/api';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    eyeDetails: {
      rightEye: {
        sphere: '',
        cylinder: '',
        axis: '',
        add: '',
        pd: ''
      },
      leftEye: {
        sphere: '',
        cylinder: '',
        axis: '',
        add: '',
        pd: ''
      },
      notes: ''
    }
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const res = await customerService.getCustomerById(id);
      console.log("res",res);
      
      setFormData({
        name: res.data.customer.name || '',
        phone: res.data.customer.phone || '',
        email: res.data.customer.email || '',
        address: {
          street: res.data.customer.address?.street || '',
          city: res.data.customer.address?.city || '',
          state: res.data.customer.address?.state || '',
          zipCode: res.data.customer.address?.zipCode || '',
          country: res.data.customer.address?.country || ''
        },
        eyeDetails: {
          rightEye: {
            sphere: res.data.customer.eyeDetails?.rightEye?.sphere || '',
            cylinder: res.data.customer.eyeDetails?.rightEye?.cylinder || '',
            axis: res.data.customer.eyeDetails?.rightEye?.axis || '',
            add: res.data.customer.eyeDetails?.rightEye?.add || '',
            pd: res.data.customer.eyeDetails?.rightEye?.pd || ''
          },
          leftEye: {
            sphere: res.data.customer.eyeDetails?.leftEye?.sphere || '',
            cylinder: res.data.customer.eyeDetails?.leftEye?.cylinder || '',
            axis: res.data.customer.eyeDetails?.leftEye?.axis || '',
            add: res.data.customer.eyeDetails?.leftEye?.add || '',
            pd: res.data.customer.eyeDetails?.leftEye?.pd || ''
          },
          notes: res.data.customer.eyeDetails?.notes || ''
        }
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError('Failed to load customer data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else if (name.includes('rightEye.') || name.includes('leftEye.')) {
      const [eye, field] = name.split('.');
      setFormData({
        ...formData,
        eyeDetails: {
          ...formData.eyeDetails,
          [eye]: {
            ...formData.eyeDetails[eye],
            [field]: value
          }
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (isEditMode) {
        await customerService.updateCustomer(id, formData);
        toast.success('Customer updated successfully');
      } else {
        const res = await customerService.addCustomer(formData);
        toast.success('Customer added successfully');
        navigate(`/customers/${res.data.customer._id}`);
      }
      
      if (isEditMode) {
        navigate(`/customers/${id}`);
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.response?.data?.msg || 'Failed to save customer');
      toast.error(err.response?.data?.msg || 'Failed to save customer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>
            <FaUser className="me-2" />
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h1>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Basic Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="name">
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter customer's full name"
                    isInvalid={!!formErrors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="phone">
                  <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    isInvalid={!!formErrors.phone}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.phone}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address (optional)"
                    isInvalid={!!formErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Address Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="address.street">
                  <Form.Label>Street Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleChange}
                    placeholder="Enter street address"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="address.city">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="address.state">
                  <Form.Label>State/Province</Form.Label>
                  <Form.Control
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleChange}
                    placeholder="Enter state or province"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="address.zipCode">
                  <Form.Label>Zip/Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleChange}
                    placeholder="Enter zip or postal code"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="address.country">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleChange}
                    placeholder="Enter country"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Eye Details (Optional)</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h5 className="mb-3">Right Eye</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.sphere">
                      <Form.Label>Sphere (SPH)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.sphere"
                        value={formData.eyeDetails.rightEye.sphere}
                        onChange={handleChange}
                        placeholder="Enter sphere value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.cylinder">
                      <Form.Label>Cylinder (CYL)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.cylinder"
                        value={formData.eyeDetails.rightEye.cylinder}
                        onChange={handleChange}
                        placeholder="Enter cylinder value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.axis">
                      <Form.Label>Axis</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.axis"
                        value={formData.eyeDetails.rightEye.axis}
                        onChange={handleChange}
                        placeholder="Enter axis value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.add">
                      <Form.Label>Add</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.add"
                        value={formData.eyeDetails.rightEye.add}
                        onChange={handleChange}
                        placeholder="Enter add value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="rightEye.pd">
                      <Form.Label>PD (Pupillary Distance)</Form.Label>
                      <Form.Control
                        type="text"
                        name="rightEye.pd"
                        value={formData.eyeDetails.rightEye.pd}
                        onChange={handleChange}
                        placeholder="Enter PD value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              
              <Col md={6}>
                <h5 className="mb-3">Left Eye</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.sphere">
                      <Form.Label>Sphere (SPH)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.sphere"
                        value={formData.eyeDetails.leftEye.sphere}
                        onChange={handleChange}
                        placeholder="Enter sphere value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.cylinder">
                      <Form.Label>Cylinder (CYL)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.cylinder"
                        value={formData.eyeDetails.leftEye.cylinder}
                        onChange={handleChange}
                        placeholder="Enter cylinder value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.axis">
                      <Form.Label>Axis</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.axis"
                        value={formData.eyeDetails.leftEye.axis}
                        onChange={handleChange}
                        placeholder="Enter axis value"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.add">
                      <Form.Label>Add</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.add"
                        value={formData.eyeDetails.leftEye.add}
                        onChange={handleChange}
                        placeholder="Enter add value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="leftEye.pd">
                      <Form.Label>PD (Pupillary Distance)</Form.Label>
                      <Form.Control
                        type="text"
                        name="leftEye.pd"
                        value={formData.eyeDetails.leftEye.pd}
                        onChange={handleChange}
                        placeholder="Enter PD value"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="eyeDetails.notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="eyeDetails.notes"
                    value={formData.eyeDetails.notes}
                    onChange={handleChange}
                    placeholder="Enter any additional notes about the customer's eye details"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-end mb-4">
          <Button 
            variant="secondary" 
            as={Link} 
            to={isEditMode ? `/customers/${id}` : '/customers'} 
            className="me-2"
          >
            <FaTimes className="me-1" /> Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={isSubmitting}
          >
            <FaSave className="me-1" /> {isSubmitting ? 'Saving...' : 'Save Customer'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default CustomerForm;
